import cloneDeep from 'lodash/cloneDeep'
import { integrationSlugs } from '@global/constants'
import { api } from 'api'
import * as SnackbarActions from '../../../global/snackbar/snackbar.actions'
import { t } from '@lingui/core/macro'

const toCamel = s => {
  return s.replace(/([-_][a-z])/gi, $1 => {
    return $1.toUpperCase().replace('-', '').replace('_', '')
  })
}

const jsonFields = {
  included_output_fields: true,
  includedOutputFields: true,
  file_type: true,
  fileType: true,
}

function showFailedToSaveError(err, dispatch, message) {
  dispatch(
    SnackbarActions.showSnackbar({
      type: 'ERROR',
      duration: 5000,
      message: err?.response?.data?.message || message,
    }),
  )
}

export function setSavingTemplate(value) {
  return dispatch => {
    dispatch({ type: 'SET_SAVING_TEMPLATE', payload: value })
  }
}

export function saveTemplate(templateState, { afterSave }) {
  return dispatch => {
    let template = null
    dispatch(setSavingTemplate(true))
    try {
      template = transformTemplate(templateState)
    } catch (err) {
      dispatch(setSavingTemplate(false))
      showFailedToSaveError(err, dispatch, t`Template failed to save.`)
      return Promise.reject(err)
    }

    return api
      .put('/api/v1/account/template/', template)
      .then(res => {
        const templateId = res.data.id
        if (template?.settings?.require_approval) {
          saveApprovers({
            approvers: template.approvers,
            templateId,
            dispatch,
          })
        } else {
          deleteApprovers({
            templateId,
            dispatch,
          })
        }

        return templateId
      })
      .catch(err => {
        showFailedToSaveError(err, dispatch, t`Template failed to save.`)
      })
      .finally(() => {
        dispatch(setSavingTemplate(false))
        afterSave?.()
      })
  }
}

function saveApprovers({ approvers, templateId, dispatch }) {
  return api
    .post(`/api/v1/account/approvers/add`, {
      template_id: templateId,
      approvers,
    })
    .catch(err => {
      dispatch(setSavingTemplate(false))
      showFailedToSaveError(err, dispatch, t`Failed to save approvers`)
    })
}

function deleteApprovers({ templateId, dispatch }) {
  return api.delete(`/api/v1/account/approvers/${templateId}`).catch(err => {
    dispatch(setSavingTemplate(false))
    showFailedToSaveError(err, dispatch, t`Failed to delete approvers`)
  })
}

export function resetTemplateChangedState() {
  return dispatch => {
    dispatch({ type: 'RESET_TEMPLATE_CHANGED_STATE' })
  }
}

export function resetStore() {
  return dispatch => {
    dispatch({ type: 'RESET_TEMPLATE_STORE' })
  }
}

export function readyToValidate() {
  return dispatch => {
    dispatch({ type: 'READY_TO_VALIDATE' })
  }
}

export function initTemplate(emailIntegrationId) {
  return dispatch => {
    dispatch({ type: 'INIT_TEMPLATE', payload: { emailIntegrationId } })
  }
}

export function setTemplateDetails(templateDetails) {
  return dispatch => {
    dispatch({ type: 'SET_TEMPLATE_DETAILS', payload: templateDetails })
  }
}

export function setCreativeConfiguration(creativeConfiguration) {
  return dispatch => {
    dispatch({ type: 'SET_CREATIVE_CONFIGURATION', payload: creativeConfiguration })
  }
}

export function setCreativeThreshold(threshold) {
  return dispatch => {
    dispatch({ type: 'SET_CREATIVE_THRESHOLD', payload: threshold })
  }
}

export function setIntegrations(integrations) {
  return dispatch => {
    dispatch({ type: 'SET_INTEGRATIONS', payload: integrations })
  }
}

export function setIntegrationsLoaded(integrations) {
  return dispatch => {
    dispatch({ type: 'SET_INTEGRATIONS_LOADED', payload: integrations })
  }
}

export function setFields(fields) {
  return dispatch => {
    dispatch({ type: 'SET_FIELDS', payload: fields })
  }
}

export function setTemplate(template) {
  return dispatch => {
    dispatch({ type: 'SET_TEMPLATE', payload: template })
  }
}

export function updateTemplateField(tf) {
  return dispatch => {
    dispatch({ type: 'UPDATE_TEMPLATE_FIELD', payload: tf })
  }
}

export function removeTemplateField(tf) {
  return dispatch => {
    dispatch({ type: 'REMOVE_TEMPLATE_FIELD', payload: tf })
  }
}

export function addFields(fields) {
  return dispatch => {
    dispatch({ type: 'ADD_FIELDS', payload: fields })
  }
}

export function addFieldsets(fieldsets) {
  return dispatch => {
    dispatch({ type: 'ADD_FIELD_SETS', payload: fieldsets })
  }
}

export function addLinkField() {
  return dispatch => {
    dispatch({ type: 'ADD_LINK_FIELD' })
  }
}

export function addParameter() {
  return dispatch => {
    dispatch({ type: 'ADD_PARAMETER' })
  }
}

export function toggleCreativeClassification(isEnabled) {
  return dispatch => {
    dispatch({ type: 'TOGGLE_CREATIVE_CLASSIFICATION', payload: isEnabled })
  }
}

export function removeParameter(param) {
  return dispatch => {
    dispatch({ type: 'REMOVE_PARAMETER', payload: param })
  }
}

export function setParameters(params) {
  return dispatch => {
    dispatch({ type: 'SET_PARAMETERS', payload: params })
  }
}

export function setParameter(param) {
  return dispatch => {
    dispatch({ type: 'SET_PARAMETER', payload: param })
  }
}

export function setDomains(domains) {
  return dispatch => {
    dispatch({ type: 'SET_DOMAINS', payload: domains })
  }
}

export function updateValidation(validation) {
  return dispatch => {
    dispatch({ type: 'UPDATE_VALIDATION', payload: validation })
  }
}

export function addValidation(validation) {
  return dispatch => {
    dispatch({ type: 'ADD_VALIDATION', payload: validation })
  }
}

export function resetValidations(validations) {
  return dispatch => {
    dispatch({ type: 'RESET_VALIDATIONS', payload: validations })
  }
}

export function removeValidation(validation) {
  return dispatch => {
    dispatch({ type: 'REMOVE_VALIDATION', payload: validation })
  }
}

export function refreshPattern(tf, pattern) {
  return dispatch => {
    dispatch({ type: 'REFRESH_PATTERN', payload: { tf, pattern } })
  }
}

export function updateIntegrationNotification(integrationId, notification) {
  return dispatch => {
    dispatch({ type: 'UPDATE_INTEGRATION_NOTIFICATION', payload: { integrationId, notification } })
  }
}

export function transformTemplate(templateState) {
  const state = cloneDeep(templateState)
  const serverModel = {
    ...state.template,
  }

  const usedFieldSets = Object.values(state.templateFieldsById).reduce((memo, item) => {
    if (item.field?.fieldset_id) {
      memo.push(item.field.fieldset_id)
    }
    return memo
  }, [])

  serverModel.template_fields = state.templateFields.map((tff, index) => {
    const tf = state.templateFieldsById[tff]
    if (typeof tf.id === 'string' && tf.id.includes('TEMP_')) {
      tf.id = null
    }
    if (typeof tf.field_id === 'string' && tf.field_id.includes('TEMP_')) {
      tf.field_id = null
      tf.field.id = null
    }
    tf.precedence = index
    return tf
  })

  serverModel.link_validations = state.validations
  serverModel.fieldsets = state.fieldsets.filter(fs => usedFieldSets.includes(fs.id))

  // remove parameters that don't have a template_feild_id OR parameter name
  // we actually only use field_ids locally, so this is breaking stuff. Should be field_id not template_field_id
  state.parameters = state.parameters.filter(p => p.field_id || p.name)

  serverModel.parameters = state.parameters.map((param, index) => {
    if (typeof param.id === 'string' && param.id.includes('TEMP_')) {
      param.id = null
    }
    if (typeof param.template_field_id === 'string' && param.template_field_id.includes('TEMP_')) {
      param.template_field_id = null
    }
    param.precedence = index
    return param
  })

  serverModel.integrations = transformInputIntegrations(
    state.integrations.map(integration =>
      Object.keys(integration).reduce(
        (acc, key) => ({
          ...acc,
          id: integration.id.includes && integration.id.includes('TEMP_') ? null : integration.id,
          [toCamel(key)]:
            jsonFields[key] && typeof integration[key] === 'string'
              ? JSON.parse(integration[key])
              : integration[key],
        }),
        {},
      ),
    ),
  )
  serverModel.integrations = serverModel.integrations.sort(a => {
    if (a.integrationType === 'INPUT') {
      return -1
    }
    return 1
  })
  serverModel.integrationNotifications = Object.keys(templateState.integrationNotifications).map(
    integrationAssignmentId => {
      const notification = { ...templateState.integrationNotifications[integrationAssignmentId] }
      notification.emailTo = JSON.stringify(notification.emailTo)
      notification.integrationAssignmentId = integrationAssignmentId
      return notification
    },
  )

  return serverModel
}

const transformInputIntegrations = integrations => {
  const transformedIntegrations = integrations.map(i => {
    // IF the integration configuration does NOT include the slug THEN fail so that we can fix this problem
    if (!i.slug) {
      throw new Error(
        `Integration transformation failed because integration configuration does NOT include integration slug: ${JSON.stringify(
          i,
        )}`,
      )
    }

    // adobe inbound
    if (i.slug === integrationSlugs.ADOBE_INBOUND) {
      const {
        adAccountsLinked: _adAccountsLinked,
        realmLinked: _realmLinked,
        reportLinked: _reportLinked,
        ...rest
      } = i.metadata
      const fieldMapping = Object.keys(rest).map(key => {
        return { adobeParameterName: key, claravineTemplateFieldId: rest[key] }
      })

      i.metadata = {
        realmLinked: i.metadata.realmLinked,
        reportLinked: i.metadata.reportLinked,
        fieldMapping,
      }
    }

    // DCM inbound
    if (i.slug === integrationSlugs.GOOGLE_DCM) {
      i = {
        ...i,
        metadata: Object.keys(i.metadata).reduce((memo, item) => {
          if (item) {
            memo[item] = i.metadata[item]
          }
          return memo
        }, {}),
      }
    }

    // bitly
    if (i.slug === integrationSlugs.BITLY) {
      i = {
        ...i,
        ...i.metadata,
      }
    }

    // facebook inbound
    if (i.slug === integrationSlugs.FACEBOOK_ADS) {
      const { adAccountsLinked, ...rest } = i.metadata

      const adAccountsLinkedArrayified =
        typeof adAccountsLinked === 'string' ? adAccountsLinked.split(',') : adAccountsLinked

      const fieldMapping = Object.keys(rest).map(key => {
        return { fbParameterName: key, claravineTemplateFieldId: rest[key] }
      })

      i.metadata = {
        adAccountsLinked: adAccountsLinkedArrayified,
        fieldMapping,
      }
    }

    // workfront inbound
    if (i.slug === integrationSlugs.WORKFRONT_PROJECT_PULL_TASK_TRIGGER) {
      i.metadata = {
        workfrontTemplateId: i.metadata.workfrontTemplateId,
        workfrontTemplateTaskId: i.metadata.workfrontTemplateTaskId,
        campaignDataMappedFields: i.metadata.campaignDataMappedFields,
      }
    }

    return i
  })

  return transformedIntegrations
}
